/**
 * One employee-search rule for every search box in the app
 * (PRD_EmployeeSearchStandardization C1–C3).
 *
 * Both the employee's searchable text and the query are lower-cased and every
 * run of characters that is not a letter, digit or "&" becomes a single space.
 * A row matches when EVERY word of the query appears somewhere in that text, in
 * any order — so "juan dela" finds "Dela Cruz, Juan", "ma. cristina" finds
 * "Ma. Cristina Reyes", "R&D" stays one word, and a bare "," or "-" finds
 * nobody. Unicode-aware so "peña" keeps its Ñ.
 *
 * Department is NOT in the text by default: a screen with a department dropdown
 * filters by department there. A screen without one passes it as an extra term
 * — matchesEmployeeSearch(e, q, [employeeDepartment(e)]) (C9).
 */

const SEPARATORS = /[^\p{L}\p{N}&]+/gu;

const normalize = (value) =>
  String(value ?? "").toLowerCase().replace(SEPARATORS, " ").trim();

/**
 * The employee's name, from whichever shape the caller holds (C2). The
 * first+last term is trimmed so an employee with neither falls through to
 * `employeeName` instead of stopping on " " — OT Approval rows carry only that.
 */
export const employeeFullName = (employee) => {
  if (!employee) return "";
  const info = employee.personalInfo || {};
  return (
    info.fullName ||
    employee.fullName ||
    `${info.firstName || ""} ${info.lastName || ""}`.trim() ||
    employee.employeeName ||
    ""
  );
};

/** The employee's department, nested or flattened (C2) — passed as an extra term (C9). */
export const employeeDepartment = (employee) =>
  employee?.employment?.department || employee?.department || "";

/**
 * Name + employee ID, plus any surface-specific extras (department, a loan name,
 * an adjustment's description). Extras join the SAME text so a query spanning
 * an employee and an extra still matches (C3).
 */
export const employeeSearchHaystack = (employee, extraTerms = []) =>
  normalize(
    [
      employeeFullName(employee),
      employee?.employeeId || employee?.employeeCode || employee?.id || "",
      ...extraTerms,
    ].join(" "),
  );

/** True when every word of `query` appears in the employee's haystack; an empty query matches all. */
export const matchesEmployeeSearch = (employee, query, extraTerms = []) => {
  const raw = String(query ?? "").trim();
  if (!raw) return true;
  const words = normalize(raw).split(" ").filter(Boolean);
  if (words.length === 0) return false; // punctuation only — matches nobody
  const haystack = employeeSearchHaystack(employee, extraTerms);
  return words.every((word) => haystack.includes(word));
};
